import { cva, type VariantProps } from 'class-variance-authority'
import {
	Radio as AriaRadio,
	RadioGroup as AriaRadioGroup,
	type RadioGroupProps as AriaRadioGroupProps,
	type RadioProps as AriaRadioProps,
} from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Radio}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Supports three sizes: sm (h-4 w-4), md (h-5 w-5, default), lg (h-6 w-6)
 * - Focus ring uses the brand primary color
 * - Selected state uses the brand primary background
 *
 * @example
 * ```tsx
 * const classes = radioVariants({ size: 'md' })
 * // Returns: "h-5 w-5 rounded-ds-full border ..."
 * ```
 */
export const radioVariants = cva(
	'rounded-ds-full border-2 border-ds-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 group-data-[selected]:border-ds-primary',
	{
		variants: {
			size: {
				sm: 'h-4 w-4',
				md: 'h-5 w-5',
				lg: 'h-6 w-6',
			},
		},
		defaultVariants: { size: 'md' },
	},
)

/**
 * Props for the {@link Radio} component.
 *
 * Extends React Aria's RadioProps with CVA size variants.
 *
 * @property size - Radio size: 'sm' (h-4 w-4) | 'md' (h-5 w-5, default) | 'lg' (h-6 w-6)
 * @property className - Optional additional Tailwind classes
 * @property children - Label text
 * @property value - Radio value
 * @property isDisabled - Disabled state (React Aria pattern)
 *
 * @example
 * ```tsx
 * <Radio value="option1" size="md">
 *   Option 1
 * </Radio>
 * ```
 */
export interface RadioProps extends Omit<AriaRadioProps, 'className'>, VariantProps<typeof radioVariants> {
	className?: string
}

/**
 * Accessible radio button primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Keyboard navigation (Arrow keys, Space to select), focus management,
 *   and ARIA attributes provided by React Aria's `<Radio>` primitive.
 * - **Styling:** All colors, spacing, and sizing use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Sizes:** Small (h-4 w-4), medium (h-5 w-5, default), large (h-6 w-6).
 * - **Focus indicator:** 2px ring in the brand primary color.
 * - **Selected state:** the brand primary color border with inner circle.
 * - **Usage:** Must be wrapped in a {@link RadioGroup}.
 *
 * @example Basic usage
 * ```tsx
 * import { RadioGroup, Radio } from '@atlas/ui-primitives'
 *
 * function MyForm() {
 *   return (
 *     <RadioGroup aria-label="Choose an option">
 *       <Radio value="option1">Option 1</Radio>
 *       <Radio value="option2">Option 2</Radio>
 *       <Radio value="option3">Option 3</Radio>
 *     </RadioGroup>
 *   )
 * }
 * ```
 *
 * @see {@link radioVariants} for CVA variant configuration
 * @see {@link RadioGroup} for grouping radios
 * @see packages/ui-primitives/src/radio/radio.stories.tsx for visual examples
 */
export function Radio({ size, className, children, ...props }: RadioProps) {
	return (
		// `relative`: contains React Aria's visually-hidden `position: absolute` input.
		// Without it the input's containing block is the initial containing block, so
		// inside a scrolled container its box lands at a phantom document offset that
		// stretches the page's scroll height and gets scrolled to on focus.
		<AriaRadio {...props} className="relative flex items-center gap-2 group">
			<div className={cn(radioVariants({ size }), className)}>
				<div className="h-full w-full rounded-ds-full flex items-center justify-center">
					<div className="h-1/2 w-1/2 rounded-ds-full bg-ds-primary opacity-0 group-data-[selected]:opacity-100 transition-opacity" />
				</div>
			</div>
			{children && <span className="text-ds-base text-ds-text select-none">{children as React.ReactNode}</span>}
		</AriaRadio>
	)
}

/**
 * Props for the {@link RadioGroup} component.
 *
 * Extends React Aria's RadioGroupProps.
 *
 * @property aria-label - Accessible name for the radio group (a visible name needs a Label child)
 * @property children - Radio elements
 * @property value - Controlled selected value
 * @property onChange - Callback when selection changes
 * @property isDisabled - Disabled state for entire group
 * @property isRequired - Required field indicator
 *
 * @example
 * ```tsx
 * <RadioGroup aria-label="Payment method" value={payment} onChange={setPayment}>
 *   <Radio value="card">Credit Card</Radio>
 *   <Radio value="paypal">PayPal</Radio>
 * </RadioGroup>
 * ```
 */
export interface RadioGroupProps extends Omit<AriaRadioGroupProps, 'className'> {
	className?: string
}

/**
 * Accessible radio group container using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Arrow key navigation between radios, Space to select.
 * - **Required:** All `<Radio>` components must be wrapped in a `<RadioGroup>`.
 * - **Single selection:** Only one radio can be selected at a time.
 *
 * @example Basic usage
 * ```tsx
 * import { RadioGroup, Radio } from '@atlas/ui-primitives'
 *
 * function MyForm() {
 *   const [value, setValue] = useState('option1')
 *
 *   return (
 *     <RadioGroup aria-label="Choose an option" value={value} onChange={setValue}>
 *       <Radio value="option1">Option 1</Radio>
 *       <Radio value="option2">Option 2</Radio>
 *     </RadioGroup>
 *   )
 * }
 * ```
 *
 * @see {@link Radio} for individual radio buttons
 * @see packages/ui-primitives/src/radio/radio.stories.tsx for visual examples
 */
export function RadioGroup({ className, children, ...props }: RadioGroupProps) {
	return (
		<AriaRadioGroup {...props} className={cn('flex flex-col gap-2', className)}>
			{children}
		</AriaRadioGroup>
	)
}
